import { useLogin } from '@ctcells/api'
import { PATHS, useSessionStore, type ModuleKey } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'
import { Navigate, useLocation, useNavigate } from 'react-router'

import { LoginScreen } from '../screens/auth/LoginScreen.tsx'

/**
 * 로그인 **컨테이너**.
 *
 * 🔴 화면(`LoginScreen`)은 한 줄도 바뀌지 않았다. 이 파일이 훅을 부르고 props 로 내려 준다 —
 *    화면과 전송을 가르는 경계가 여기다.
 *
 * 흐름:
 * ```
 * [제출] → POST /auth/login ─┬─ 200 → 토큰 저장 → 원래 가려던 곳(state.from)으로
 *                             └─ 401/403 → 화면에 사유 표시(문구는 error_codes.yaml 기준)
 * ```
 * ⚠ 모듈 패드는 이 컨테이너를 쓰지 않는다 — **PIN 로그인 API 가 계약에 없다**(`proposed.ts` ②).
 */
export interface LoginRouteProps {
  /** 모듈 앱이면 그 키. 제목이 제품명으로 바뀐다 */
  moduleKey?: ModuleKey
}

/**
 * 로그인 실패 한 줄. 🔴 `AUTH_FAILED` 만 로그인 전용 문구(「아이디·비밀번호를 확인하세요」)로 말한다 —
 * 입력칸 이름이 「아이디」라서다(eric 2026-09-28). 나머지 코드는 공용 사전 그대로다.
 */
function loginErrorMessage(error: Parameters<typeof errorMessage>[0]): string {
  return error?.error_code === 'AUTH_FAILED' ? t('auth.failed') : errorMessage(error)
}

export function LoginRoute({ moduleKey }: LoginRouteProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const status = useSessionStore((state) => state.status)
  const mutation = useLogin()

  const from = (location.state as { from?: string } | null)?.from ?? PATHS.monitoring

  // 이미 로그인돼 있으면 로그인 화면을 보여 줄 이유가 없다
  if (status === 'authenticated') return <Navigate to={from} replace />

  return (
    <LoginScreen
      {...(moduleKey === undefined ? {} : { moduleKey })}
      {...(mutation.error === null ? {} : { errorMessage: loginErrorMessage(mutation.error) })}
      /* 🔴 버튼이 비활성이 되는 유일한 갈래 — 빈 칸은 화면이 제 칸 아래 사유로 말한다 */
      pending={mutation.isPending}
      onSubmit={({ username, password, remember }) => {
        mutation.mutate(
          { username, password, remember },
          {
            onSuccess: () => {
              void navigate(from, { replace: true })
            },
          },
        )
      }}
    />
  )
}
